import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { getNeonColor, scrollable } from "../../theme";
import { useReferentialContext } from "../../Context";
import { usePlayerLinkQueue } from "../../playerLinkQueue";
import { useThemeContext } from "../../themeContext";
import { SCALE_STOPS, paletteWinLoss, scaleColor as colorForScale, scaleGlowSx, shadeHex } from "../../settings/numberScale";
import { Agent, Maps, Player, SoloQAccounts, SoloQGameSummaries, Team } from "../../reckon-api";
import { NeonCard, NeonCardContent } from "../../NeonComponents/NeonCards";
import { NeonEmptyState } from "../../NeonComponents/NeonTable";
import formatters from "../../NeonComponents/Formatters";
import TeamIcon from "../../components/Icons/TeamIcon";
import { nationalityFlagCode } from "../../components/Icons/NationalityFlag";
import PlayedAgents, { playedAgentsColumnWidth } from "../../components/Icons/PlayedAgents";
import RankIcon, { findCompetitiveTier, fullRankFromNumber, rankLabel, rankPalette } from "../../components/Icons/RankIcon";
import RoleIcon from "../../components/Icons/RoleIcon";
import FadedAgentArt, { AGENT_BG_INSET, agentArtSize } from "../../components/Icons/FadedAgentArt";
import { agentAccent, hexRgba } from "../../components/Icons/agentColors";
import WindowContainer from "../../components/layout/WindowContainer";
import DatePicker from "../../components/selectors/Shared/DateSelect";
import { MapBannerSelect } from "../../components/selectors/MapSelect";
import { TeamSelect } from "../../components/selectors/TeamSelect";
import { getMapAsset } from "../../valorant-api/mapAssets";

const STAT_AGGREGATES = [
    "win__avg",
    "pk__cnt",
    "acs__avg",
    "adr__avg",
    "kast__avg",
    "rating_vlr__avg",
    "headshot_percent__avg",
    "kills__avg",
    "deaths__avg",
];

const ROLE_ORDER = ["duelist", "initiator", "controller", "sentinel", "flex"];
const LAST_GAMES = 5;
const SERVERS = ["eu", "na", "ap", "kr", "latam", "br"];
const HEADER_BAND_HEIGHT = 124;
const CARD_FLAG_WIDTH = 128;
const ROLE_ICON_SIZE = 44;
const ROLE_ZOOM = 1.4;
const ROLE_IMG_SIZE = Math.round(ROLE_ICON_SIZE * ROLE_ZOOM);
const ROLE_CRANK = 18;
const RECENT_GAME_FIELDS = [
    "pk,date,map,agent,win,puuid,competitive_player,rounds_won,rounds_lost,rating_vlr,kills,deaths,assists,acs,rank_tier,game_id",
];

interface PlayerSoloQSummary {
    player: Player;
    account: SoloQAccounts | null;
    accounts: number;
    games: number;
    winRate: number;
    acs: number;
    kd: number;
    adr: number;
    rating: number;
    agents: string[];
    recentGames: SoloQGameSummaries[];
    suggestedAccounts: SoloQAccounts[];
}

function teamLabel(teamId?: string | null) {
    return teamId?.split("_")[0] || "";
}

function formatRoleName(role?: string | null) {
    if (!role) return "";
    const name = role.includes("::") ? role.split("::").pop() || role : role;
    return name.replace(/_/g, " ");
}

const COMBAT_ROLES = ["duelist", "initiator", "controller", "sentinel"];

function mostPlayedRole(agentNames: string[], agents: Agent[]) {
    for (const name of agentNames) {
        const agent = agents.find((entry) => entry.id.toLowerCase() === name.toLowerCase());
        const role = formatRoleName(agent?.role);
        if (role) return { role, icon: agent?.role_icon };
    }
    return undefined;
}

function resolveCardRole(playerRole: string | null | undefined, agentNames: string[], agents: Agent[]) {
    const defined = formatRoleName(playerRole);
    const key = defined.toLowerCase();
    if (defined && COMBAT_ROLES.some((role) => key.includes(role))) {
        const match = agents.find((agent) => formatRoleName(agent.role).toLowerCase() === key);
        return { role: defined, icon: match?.role_icon };
    }
    return mostPlayedRole(agentNames, agents);
}

function roleSortKey(player: Player) {
    if (player.is_sub) return 80;
    const name = formatRoleName(player.role).toLowerCase();
    const idx = ROLE_ORDER.findIndex((role) => name.includes(role));
    if (idx >= 0) return idx;
    if (name.includes("coach")) return 90;
    return 40;
}

function hsUnit(value: number) {
    return value > 1 ? value / 100 : value;
}

function numOrNull(row: any, ...keys: string[]): number | null {
    for (const key of keys) {
        if (!key) continue;
        const value = row?.[key];
        if (value != null && value !== "" && !Number.isNaN(Number(value))) return Number(value);
    }
    return null;
}

function num(row: any, ...keys: string[]): number {
    return numOrNull(row, ...keys) ?? 0;
}

function str(row: any, ...keys: string[]): string {
    for (const key of keys) {
        const value = row?.[key];
        if (value != null && value !== "") return String(value);
    }
    return "";
}

function asList<T>(result: any): T[] {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.results)) return result.results;
    return [];
}

function normalizeNameToken(value: string) {
    return value.trim().toLowerCase().split("#")[0].replace(/[^a-z0-9]+/g, "");
}

function playerNameTokens(player: Player) {
    const raw = [player.id, player.previous_names || ""]
        .flatMap((value) => value.split(/[,;/|]/))
        .map((value) => value.trim())
        .filter((value) => value.length >= 2);
    const seen = new Set<string>();
    const tokens: string[] = [];
    for (const value of raw) {
        const key = value.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        tokens.push(value);
    }
    return tokens;
}

function accountMatchScore(accountName: string, tokens: string[]) {
    const account = normalizeNameToken(accountName);
    if (account.length < 2) return 0;
    let best = 0;
    for (const token of tokens) {
        const candidate = normalizeNameToken(token);
        if (!candidate) continue;
        if (account === candidate) best = Math.max(best, 100);
        else if (account.startsWith(candidate) || candidate.startsWith(account)) best = Math.max(best, 70);
        else if (account.includes(candidate) || candidate.includes(account)) best = Math.max(best, 50);
    }
    return best;
}

async function suggestedAccountsForPlayer(
    listAccounts: (params: { additional_filters?: object; ordering?: string[] }) => Promise<any>,
    player: Player,
    excludePuuids: Set<string> = new Set(),
    options: { minScore?: number; limit?: number; server?: string | null } = {},
): Promise<SoloQAccounts[]> {
    const tokens = playerNameTokens(player).slice(0, 1);
    if (!tokens.length) return [];
    const minScore = options.minScore ?? 50;
    const limit = options.limit ?? 3;
    const serverFilter = options.server ? { server: options.server } : {};
    const lists = await Promise.all(
        tokens.map((token) =>
            listAccounts({
                additional_filters: { account_name__icontains: token, player__isnull: true, ...serverFilter },
                ordering: ["-rank_tier"],
            }).catch(() => [] as SoloQAccounts[]),
        ),
    );
    const seen = new Set<string>(excludePuuids);
    const matches: { account: SoloQAccounts; score: number }[] = [];
    for (const list of lists) {
        for (const account of asList<SoloQAccounts>(list)) {
            if (account.player && account.player !== player.id) continue;
            if (options.server && (account.server || "").toLowerCase() !== options.server.toLowerCase()) continue;
            const key = account.puuid || String(account.id);
            if (!key || seen.has(key) || (account.puuid && excludePuuids.has(account.puuid))) continue;
            const score = accountMatchScore(account.account_name, tokens);
            if (score < minScore) continue;
            seen.add(key);
            matches.push({ account, score });
        }
    }
    matches.sort((a, b) => {
        const tier = (b.account.rank_tier ?? -1) - (a.account.rank_tier ?? -1);
        if (tier) return tier;
        const rating = (b.account.ranked_rating ?? -1) - (a.account.ranked_rating ?? -1);
        if (rating) return rating;
        return b.score - a.score;
    });
    return matches.slice(0, limit).map((entry) => entry.account);
}

function asAggregateRows(result: any, groupField: string): any[] {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.results)) return result.results;
    if (result && typeof result === "object") {
        return Object.entries(result).map(([key, value]) => {
            if (value && typeof value === "object") {
                return { [groupField]: (value as any)[groupField] ?? key, ...(value as object) };
            }
            return { [groupField]: key };
        });
    }
    return [];
}

function parseAgents(value: any, limit = 3): string[] {
    if (value == null || value === "") return [];
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
            try {
                return parseAgents(JSON.parse(trimmed), limit);
            } catch {
                return trimmed.split(",").map((name) => name.trim()).filter(Boolean).slice(0, limit);
            }
        }
        return trimmed.split(",").map((name) => name.trim()).filter(Boolean).slice(0, limit);
    }
    if (Array.isArray(value)) {
        const ranked = value
            .map((item) => {
                if (typeof item === "string") return { name: item, count: 0 };
                const name = str(item, "agent", "id", "name", "key");
                const count = num(item, "count", "games", "pk__cnt", "value");
                return name ? { name, count } : null;
            })
            .filter((item): item is { name: string; count: number } => Boolean(item));
        const hasCounts = ranked.some((item) => item.count > 0);
        if (hasCounts) ranked.sort((a, b) => b.count - a.count);
        return ranked.map((item) => item.name).slice(0, limit);
    }
    if (typeof value === "object") {
        return Object.entries(value)
            .map(([key, entry]) => {
                const count = typeof entry === "number" ? entry : num(entry, "count", "games", "pk__cnt", "value");
                return { name: key, count };
            })
            .sort((a, b) => b.count - a.count)
            .map((item) => item.name)
            .filter(Boolean)
            .slice(0, limit);
    }
    return [];
}

function accountMatchesServer(account: SoloQAccounts, server: string | null) {
    if (!server) return true;
    return (account.server || "").toLowerCase() === server.toLowerCase();
}

function pickBestAccount(accounts: SoloQAccounts[]) {
    return [...accounts].sort((a, b) => {
        const tier = (b.rank_tier ?? -1) - (a.rank_tier ?? -1);
        if (tier) return tier;
        return (b.ranked_rating ?? -1) - (a.ranked_rating ?? -1);
    })[0] || null;
}

function agentFullPortraitUrl(
    agentName?: string | null,
    agents?: { id: string; uuid?: string; full_portrait?: string | null }[],
) {
    if (!agentName || !agents?.length) return undefined;
    const ref = agents.find((a) => a.id.toLowerCase() === agentName.toLowerCase());
    if (ref?.full_portrait) return ref.full_portrait;
    if (ref?.uuid) return `https://media.valorant-api.com/agents/${ref.uuid}/fullportrait.png`;
    return undefined;
}

function fadeSeed(value: string) {
    let hash = 2166136261;
    for (let i = 0; i < value.length; i += 1) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
}

function fadeUnit(seed: number, salt: number) {
    const x = Math.sin(seed * 0.0007 + salt * 12.9898) * 43758.5453;
    return x - Math.floor(x);
}

function flagObjectPosition(seed: number) {
    const x = 8 + fadeUnit(seed, 7) * 20;
    const y = 28 + fadeUnit(seed, 8) * 36;
    return `${x.toFixed(0)}% ${y.toFixed(0)}%`;
}

function mapArtUrl(mapId: string, mapRef?: Maps) {
    if (mapRef?.splash) return mapRef.splash;
    if (mapRef?.uuid) return `https://media.valorant-api.com/maps/${mapRef.uuid}/splash.png`;
    return getMapAsset(mapId, "splash").replace("/upload/", "/upload/c_fill,h_360,w_1440,q_auto/");
}

function splashMask(fadeLeft: boolean, fadeRight: boolean) {
    if (!fadeLeft && !fadeRight) return undefined;
    const stops = [
        fadeLeft
            ? "transparent 0px, transparent 28px, rgba(0,0,0,0.35) 48px, rgba(0,0,0,0.8) 72px, #000 96px"
            : "#000 0%",
        fadeRight ? "#000 58%, rgba(0,0,0,0.35) 82%, transparent 100%" : "#000 100%",
    ].join(", ");
    return `linear-gradient(90deg, ${stops})`;
}

function splashResultWash(color: string) {
    return `linear-gradient(90deg, ${hexRgba("#000000", 0.72)} 0%, ${hexRgba(color, 0.22)} 28%, ${hexRgba("#000000", 0.58)} 58%, ${hexRgba(color, 0.16)} 78%, ${hexRgba("#000000", 0.9)} 100%)`;
}

function MapSplash({
    mapId,
    mapRef,
    overlay,
    position = "center 35%",
    fadeLeft = false,
    fadeRight = false,
    tint,
}: {
    mapId: string;
    mapRef?: Maps;
    overlay: string;
    position?: string;
    fadeLeft?: boolean;
    fadeRight?: boolean;
    tint?: string;
}) {
    const mask = splashMask(fadeLeft, fadeRight);
    const maskSx = mask
        ? {
              maskImage: mask,
              WebkitMaskImage: mask,
              maskRepeat: "no-repeat",
              WebkitMaskRepeat: "no-repeat",
              maskSize: "100% 100%",
              WebkitMaskSize: "100% 100%",
          }
        : {};
    return (
        <>
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 0,
                    backgroundImage: `url(${mapArtUrl(mapId, mapRef)})`,
                    backgroundSize: "cover",
                    backgroundPosition: position,
                    backgroundRepeat: "no-repeat",
                    ...maskSx,
                }}
            />
            {tint ? (
                <>
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 1,
                            backgroundColor: shadeHex(tint, 0.42),
                            mixBlendMode: "multiply",
                            opacity: 0.82,
                            pointerEvents: "none",
                            ...maskSx,
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 1,
                            backgroundColor: tint,
                            mixBlendMode: "soft-light",
                            opacity: 0.22,
                            pointerEvents: "none",
                            ...maskSx,
                        }}
                    />
                </>
            ) : null}
            <Box sx={{ position: "absolute", inset: 0, zIndex: 1, background: overlay }} />
        </>
    );
}

function MatchStat({
    label,
    value,
    color,
}: {
    label: string;
    value: string;
    color?: string;
}) {
    return (
        <Box sx={{ textAlign: "center", minWidth: 0 }}>
            <Typography
                sx={{
                    fontWeight: 800,
                    fontSize: "0.72rem",
                    lineHeight: 1.15,
                    color: color || "rgba(255,255,255,0.92)",
                    textShadow: color
                        ? `0 0 8px ${color}55, 0 1px 4px rgba(0,0,0,0.85)`
                        : "0 1px 4px rgba(0,0,0,0.8)",
                    whiteSpace: "nowrap",
                }}
            >
                {value}
            </Typography>
            <Typography
                sx={{
                    mt: 0.1,
                    fontSize: "0.52rem",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.5)",
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}

function useScale() {
    const { numberScale } = useThemeContext();
    const { win, loss } = paletteWinLoss(numberScale);
    return {
        color: (value: number | null, stops: number[]) => colorForScale(value, stops, numberScale),
        glow: (color: string) => scaleGlowSx(color, numberScale.glow),
        win,
        loss,
    };
}

function RecentGameRow({ game, mapsById }: { game: SoloQGameSummaries; mapsById: Map<string, Maps> }) {
    const scale = useScale();
    const won = Boolean(game.win);
    const accent = won ? scale.win : scale.loss;
    const mapRef = mapsById.get((game.map || "").toLowerCase());
    const hasScore = game.rounds_won != null && game.rounds_lost != null;
    const rating = game.rating_vlr ?? 0;
    const ratingColor = scale.color(rating || null, SCALE_STOPS.rating);
    const agentArt = 56;
    const agentSlot = Math.max(AGENT_BG_INSET + agentArtSize(agentArt) - 18, 40);

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                flexShrink: 0,
                borderRadius: 1,
                overflow: "hidden",
                borderLeft: `3px solid ${accent}`,
            }}
        >
            <MapSplash
                mapId={game.map}
                mapRef={mapRef}
                position="center 42%"
                fadeLeft
                fadeRight
                tint={accent}
                overlay={splashResultWash(accent)}
            />
            <FadedAgentArt agent={game.agent} size={agentArt} asBackground sx={{ left: -18 }} />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    pr: 0.85,
                    pl: 0,
                    py: 0.4,
                    minHeight: 48,
                }}
            >
                <Box sx={{ width: agentSlot, minWidth: agentSlot, flexShrink: 0 }} />
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "grid",
                        gridTemplateColumns: "56px minmax(0, 1fr) 44px",
                        columnGap: 0.5,
                        alignItems: "center",
                    }}
                >
                    <Box sx={{ textAlign: "center" }}>
                        {hasScore ? (
                            <Stack direction="row" spacing={0.25} sx={{ alignItems: "center", justifyContent: "center" }}>
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: "0.88rem",
                                        color: won ? scale.win : scale.loss,
                                        textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                                    }}
                                >
                                    {game.rounds_won}
                                </Typography>
                                <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.7rem" }}>-</Typography>
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: "0.88rem",
                                        color: won ? scale.loss : scale.win,
                                        textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                                    }}
                                >
                                    {game.rounds_lost}
                                </Typography>
                            </Stack>
                        ) : (
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    fontSize: "0.68rem",
                                    color: won ? scale.win : scale.loss,
                                }}
                            >
                                {won ? "WIN" : "LOSS"}
                            </Typography>
                        )}
                    </Box>
                    <MatchStat label="K/D/A" value={`${game.kills ?? 0}/${game.deaths ?? 0}/${game.assists ?? 0}`} />
                    <MatchStat
                        label="Rating"
                        value={game.rating_vlr != null ? formatters.float2(rating) : "—"}
                        color={ratingColor}
                    />
                </Box>
            </Box>
        </Box>
    );
}

function StatBlock({
    label,
    value,
    color,
}: {
    label: string;
    value: string;
    color?: string;
}) {
    const scale = useScale();
    return (
        <Box sx={{ textAlign: "center", minWidth: 0 }}>
            <Typography
                sx={{
                    fontSize: "0.56rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.4)",
                    mb: 0.2,
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontWeight: 800,
                    fontSize: "0.95rem",
                    lineHeight: 1.15,
                    ...(color ? scale.glow(color) : { color: "#fff", fontVariantNumeric: "tabular-nums" }),
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

function PlayerCard({
    row,
    onClick,
    onLinkAccount,
}: {
    row: PlayerSoloQSummary;
    onClick: () => void;
    onLinkAccount: (player: Player, account: SoloQAccounts) => void;
}) {
    const ctx = useReferentialContext();
    const { isPending } = usePlayerLinkQueue();
    const scale = useScale();
    const agentNames = [
        ...row.agents,
        ...row.recentGames.map((game) => game.agent).filter((name): name is string => Boolean(name)),
    ];
    const playedRole = resolveCardRole(row.player.role, agentNames, ctx.agentsReferential);
    const role = playedRole?.role || "";
    const roleIcon = playedRole?.icon;
    const tier = findCompetitiveTier(ctx.competitiveTiersReferential, row.account?.rank_tier);
    const palette = rankPalette(tier, row.account?.rank_tier);
    const accent = agentAccent(row.agents[0]) || palette.accent;
    const portrait = agentFullPortraitUrl(row.agents[0], ctx.agentsReferential);
    const flagCode = nationalityFlagCode(row.player.nationality);
    const fadeKey = fadeSeed(`${row.player.id}:${flagCode || ""}:${accent || ""}`);
    const agentsWidth = playedAgentsColumnWidth(Math.max(row.agents.length, 1), 44, undefined, 20);
    const wrColor = scale.color(row.games ? row.winRate : null, SCALE_STOPS.rate);
    const acsColor = scale.color(row.games ? row.acs : null, SCALE_STOPS.acs);
    const kdColor = scale.color(row.games ? row.kd : null, SCALE_STOPS.kd);
    const adrColor = scale.color(row.games ? row.adr : null, SCALE_STOPS.adr);
    const ratingColor = scale.color(row.games ? row.rating || null : null, SCALE_STOPS.rating);
    const mapsById = useMemo(() => {
        const byId = new Map<string, Maps>();
        for (const map of ctx.mapsReferential) byId.set(map.id.toLowerCase(), map);
        return byId;
    }, [ctx.mapsReferential]);

    return (
        <NeonCard
            glow_color={accent}
            onClick={onClick}
            sx={{
                flex: "1 1 0",
                minWidth: 220,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
                overflow: "hidden",
                "&:hover": {
                    transform: "translateY(-2px)",
                    transition: "transform 0.2s ease",
                },
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: HEADER_BAND_HEIGHT,
                    minHeight: HEADER_BAND_HEIGHT,
                    maxHeight: HEADER_BAND_HEIGHT,
                    flexShrink: 0,
                    overflow: "hidden",
                    p: 0,
                    m: 0,
                    isolation: "isolate",
                }}
            >
                {flagCode ? (
                    <Box
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: CARD_FLAG_WIDTH,
                            zIndex: 0,
                            pointerEvents: "none",
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            component="img"
                            alt=""
                            src={`https://flagcdn.com/w320/${flagCode}.png`}
                            srcSet={`https://flagcdn.com/w160/${flagCode}.png 1x, https://flagcdn.com/w320/${flagCode}.png 2x, https://flagcdn.com/w640/${flagCode}.png 3x`}
                            sx={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: flagObjectPosition(fadeKey),
                                opacity: 0.72,
                                filter: "saturate(0.45) brightness(0.72) contrast(0.9)",
                            }}
                        />
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                background: [
                                    "linear-gradient(90deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.28) 38%, rgba(0,0,0,0.78) 100%)",
                                    "linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.08) 40%, rgba(0,0,0,0.55) 100%)",
                                ].join(", "),
                            }}
                        />
                    </Box>
                ) : null}
                {portrait ? (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            bottom: 0,
                            zIndex: 1,
                            pointerEvents: "none",
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            component="img"
                            src={portrait}
                            alt=""
                            sx={{
                                position: "absolute",
                                left: "86%",
                                top: "54%",
                                height: "150%",
                                width: "auto",
                                maxWidth: "none",
                                transform: "translate(-50%, -50%)",
                                objectFit: "contain",
                                filter: "brightness(1.12) saturate(1.1)",
                                userSelect: "none",
                            }}
                        />
                    </Box>
                ) : null}
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 3,
                        width: "100%",
                        height: "100%",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            top: 0,
                            zIndex: 4,
                            px: 1.5,
                            pt: 1.5,
                            pb: 1.25,
                            background:
                                "linear-gradient(180deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.38) 58%, transparent 100%)",
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 800,
                                fontSize: "1.05rem",
                                lineHeight: 1.15,
                                textShadow:
                                    "0 1px 2px rgba(0,0,0,0.95), 0 0 10px rgba(0,0,0,0.9), 0 2px 14px rgba(0,0,0,0.75)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {row.player.id}
                        </Typography>
                        <Typography
                            sx={{
                                mt: 0.25,
                                fontSize: "0.62rem",
                                fontWeight: 600,
                                color: "rgba(255,255,255,0.72)",
                                textShadow:
                                    "0 1px 2px rgba(0,0,0,0.95), 0 0 8px rgba(0,0,0,0.85)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {row.account?.account_name || (row.accounts === 0 ? "No SoloQ account" : "Unranked")}
                            {row.player.is_sub ? " · Sub" : ""}
                        </Typography>
                    </Box>

                    <Stack
                        spacing={0.2}
                        sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 6,
                            alignItems: "center",
                            textAlign: "center",
                            pointerEvents: "none",
                        }}
                    >
                        {row.account ? (
                            <>
                                <RankIcon
                                    tier={row.account.rank_tier}
                                    rating={row.account.ranked_rating}
                                    size={44}
                                />
                                <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", lineHeight: 1.15, color: palette.accent }}>
                                    {rankLabel(tier) ||
                                        fullRankFromNumber(
                                            typeof row.account.rank_tier === "number" ? row.account.rank_tier : null,
                                        )}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "0.58rem",
                                        fontWeight: 600,
                                        color: "rgba(255,255,255,0.48)",
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {[
                                        row.account.server ? row.account.server.toUpperCase() : "",
                                        row.account.ranked_rating != null ? `${row.account.ranked_rating} RR` : "",
                                    ]
                                        .filter(Boolean)
                                        .join(" · ") || "—"}
                                </Typography>
                            </>
                        ) : (
                            <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.4)", fontWeight: 700 }}>
                                Untracked
                            </Typography>
                        )}
                    </Stack>
                </Box>
            </Box>
            <NeonCardContent
                sx={{
                    position: "relative",
                    zIndex: 3,
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    overflow: "hidden",
                    isolation: "isolate",
                    pt: 0.5,
                    pb: "16px !important",
                    px: 1.5,
                }}
            >
                <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                        alignSelf: "stretch",
                        flexShrink: 0,
                        height: ROLE_IMG_SIZE,
                        width: "calc(100% + 24px)",
                        ml: -1.5,
                        position: "relative",
                        zIndex: 4,
                    }}
                >
                    {role ? (
                        <Box
                            sx={{
                                position: "relative",
                                width: ROLE_IMG_SIZE,
                                minWidth: ROLE_IMG_SIZE,
                                height: ROLE_IMG_SIZE,
                                flexShrink: 0,
                                overflow: "visible",
                                zIndex: 5,
                            }}
                        >
                            <RoleIcon
                                role={role}
                                icon={roleIcon}
                                size={ROLE_IMG_SIZE}
                                sx={{
                                    position: "absolute",
                                    left: -ROLE_CRANK,
                                    top: 0,
                                    zIndex: 5,
                                    filter:
                                        "brightness(1.28) drop-shadow(0 0 8px rgba(0,0,0,0.95)) drop-shadow(0 2px 4px rgba(0,0,0,0.9))",
                                }}
                            />
                        </Box>
                    ) : (
                        <Box />
                    )}
                    <Box
                        sx={{
                            position: "relative",
                            width: agentsWidth,
                            height: 52,
                            flexShrink: 0,
                            overflow: "hidden",
                            mr: 0,
                        }}
                    >
                        <PlayedAgents agents={row.agents} size={44} overlap={20} />
                    </Box>
                </Stack>

                <Stack spacing={0.4} sx={{ width: "100%", mt: 1, position: "relative", zIndex: 3 }}>
                    {row.recentGames.length ? (
                        row.recentGames.map((game) => (
                            <RecentGameRow key={game.pk || `${game.game_id}-${game.puuid}`} game={game} mapsById={mapsById} />
                        ))
                    ) : !row.suggestedAccounts.length ? (
                        <Typography sx={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.35)", textAlign: "center", py: 0.5 }}>
                            {row.accounts === 0 ? "No matching accounts" : "No recent games"}
                        </Typography>
                    ) : null}
                    {row.suggestedAccounts.length ? (
                        <Stack
                            spacing={0.5}
                            sx={{ width: "100%", pt: row.recentGames.length ? 0.5 : 0 }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <Typography
                                sx={{
                                    fontSize: "0.58rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    color: "rgba(255,255,255,0.4)",
                                    textAlign: "center",
                                }}
                            >
                                Possible accounts
                            </Typography>
                            {row.suggestedAccounts.map((account) => {
                                const busy = Boolean(account.puuid && isPending(account.puuid));
                                const suggestedTier = findCompetitiveTier(
                                    ctx.competitiveTiersReferential,
                                    account.rank_tier,
                                );
                                const elo =
                                    rankLabel(suggestedTier) ||
                                    fullRankFromNumber(
                                        typeof account.rank_tier === "number" ? account.rank_tier : null,
                                    );
                                const server = (account.server || "").toUpperCase();
                                return (
                                    <Stack
                                        key={account.puuid || account.id}
                                        direction="row"
                                        spacing={0.5}
                                        sx={{ alignItems: "center", minWidth: 0 }}
                                    >
                                        <Typography
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                fontSize: "0.68rem",
                                                fontWeight: 700,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {account.account_name || "Unknown"}
                                        </Typography>
                                        {server ? (
                                            <Typography
                                                sx={{
                                                    fontSize: "0.55rem",
                                                    fontWeight: 700,
                                                    letterSpacing: "0.04em",
                                                    color: "rgba(255,255,255,0.45)",
                                                    whiteSpace: "nowrap",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {server}
                                            </Typography>
                                        ) : null}
                                        <Typography
                                            sx={{
                                                fontSize: "0.58rem",
                                                fontWeight: 700,
                                                color: "rgba(255,255,255,0.55)",
                                                fontVariantNumeric: "tabular-nums",
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {elo || "—"}
                                            {account.ranked_rating != null ? ` ${account.ranked_rating}` : ""}
                                        </Typography>
                                        <Button
                                            size="small"
                                            disabled={busy}
                                            onClick={() => onLinkAccount(row.player, account)}
                                            sx={{
                                                fontWeight: 700,
                                                textTransform: "none",
                                                minWidth: 0,
                                                px: 0.75,
                                                minHeight: 24,
                                                fontSize: "0.62rem",
                                                border: "1px solid rgba(255,255,255,0.28)",
                                                color: "#fff",
                                            }}
                                        >
                                            {busy ? "Queued" : "Link"}
                                        </Button>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    ) : null}
                </Stack>

                <Box
                    sx={{
                        mt: "auto",
                        width: "100%",
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        rowGap: 1.1,
                        columnGap: 0.5,
                        pt: 1.25,
                    }}
                >
                    <StatBlock label="G" value={row.games ? String(row.games) : "—"} />
                    <StatBlock label="WR" value={row.games ? formatters.percent(row.winRate) : "—"} color={wrColor} />
                    <StatBlock label="ACS" value={row.games ? formatters.integer(row.acs) : "—"} color={acsColor} />
                    <StatBlock label="KD" value={row.games ? formatters.float2(row.kd) : "—"} color={kdColor} />
                    <StatBlock label="ADR" value={row.games ? formatters.integer(row.adr) : "—"} color={adrColor} />
                    <StatBlock
                        label="Rating"
                        value={row.games && row.rating ? formatters.float2(row.rating) : "—"}
                        color={ratingColor}
                    />
                </Box>
            </NeonCardContent>
        </NeonCard>
    );
}

export default function SoloQTeamSummary() {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const ctx = useReferentialContext();
    const { enqueuePlayerLink } = usePlayerLinkQueue();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const teamParam = searchParams.get("team");

    const [team, setTeam] = useState<Team | null>(ctx.selectedTeam);
    const [from, setFrom] = useState(dayjs().subtract(3, "month"));
    const [until, setUntil] = useState(dayjs());
    const [mapSelected, setMapSelected] = useState<Maps | null>(null);
    const [serverSelected, setServerSelected] = useState<string | null>(null);
    const [mapsWithGames, setMapsWithGames] = useState<string[] | null>(null);
    const [rows, setRows] = useState<PlayerSoloQSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);
    const req = useRef(0);
    const mountedRef = useRef(true);
    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    useEffect(() => {
        if (teamParam) {
            const wanted = decodeURIComponent(teamParam).toLowerCase();
            const match = ctx.teamsReferential.find(
                (entry) => entry.id.toLowerCase() === wanted || entry.name.toLowerCase() === wanted,
            );
            if (match) {
                setTeam((current) => (current?.id === match.id ? current : match));
                if (ctx.selectedTeam?.id !== match.id) {
                    setMapSelected(null);
                    ctx.setSelectedTeam(match);
                }
            }
            return;
        }
        if (!team && ctx.selectedTeam) setTeam(ctx.selectedTeam);
    }, [teamParam, ctx.teamsReferential, ctx.selectedTeam, team]);

    const roster = useMemo(() => {
        if (!team) return [] as Player[];
        return ctx.playersReferential
            .filter((player) => player.current_team === team.id || player.current_team === team.name)
            .sort((a, b) => roleSortKey(a) - roleSortKey(b) || a.id.localeCompare(b.id));
    }, [team, ctx.playersReferential]);
    const rosterKey = useMemo(() => roster.map((player) => player.id).join(","), [roster]);
    const loadedRosterKey = useRef("");

    useEffect(() => {
        if (!team) {
            req.current += 1;
            loadedRosterKey.current = "";
            setRows([]);
            setMapsWithGames(null);
            setLoading(false);
            return;
        }
        if (!roster.length) {
            req.current += 1;
            loadedRosterKey.current = "";
            setRows([]);
            setMapsWithGames(null);
            setLoading(ctx.playersReferential.length === 0);
            return;
        }

        const requestId = ++req.current;
        const teamChanged = loadedRosterKey.current !== rosterKey;
        if (teamChanged) {
            loadedRosterKey.current = rosterKey;
            setRows([]);
            setMapsWithGames(null);
        }
        setLoading(true);

        const agentsMetric = ctx.metricsReferential.find((metric) => metric.code.toLowerCase() === "agents");
        const playerIds = roster.map((player) => player.id);
        const playerIdSet = new Set(playerIds);

        const listRosterAccounts = async () => {
            try {
                const result = await ctx.reckonApi.soloQAccounts.list({
                    ordering: ["-rank_tier"],
                    additional_filters: { player__in: playerIds.join(",") },
                });
                const rows = asList<SoloQAccounts>(result);
                if (rows.some((account) => account.player && !playerIdSet.has(account.player))) {
                    throw new Error("unfiltered");
                }
                return rows;
            } catch {
                const lists = await Promise.all(
                    roster.map((player) =>
                        ctx.reckonApi.soloQAccounts
                            .list({ player: player.id, ordering: ["-rank_tier"] })
                            .then((result) => asList<SoloQAccounts>(result))
                            .catch(() => [] as SoloQAccounts[]),
                    ),
                );
                return lists.flat();
            }
        };

        listRosterAccounts()
            .then(async (allLinkedAccounts) => {
                if (requestId !== req.current) return;
                const accountsByPlayer = new Map<string, SoloQAccounts[]>();
                const linkedByPlayer = new Map<string, Set<string>>();
                const puuids: string[] = [];
                for (const player of roster) {
                    accountsByPlayer.set(player.id, []);
                    linkedByPlayer.set(player.id, new Set());
                }
                for (const account of allLinkedAccounts) {
                    const playerId = account.player || "";
                    if (!playerId || !accountsByPlayer.has(playerId)) continue;
                    if (account.puuid) linkedByPlayer.get(playerId)!.add(account.puuid);
                    if (!accountMatchesServer(account, serverSelected)) continue;
                    accountsByPlayer.get(playerId)!.push(account);
                    if (account.puuid) puuids.push(account.puuid);
                }

                const baseFilters: Record<string, string> = {
                    date__gte: from.toISOString(),
                    date__lt: until.toISOString(),
                };
                if (serverSelected) baseFilters.account__server = serverSelected;
                if (puuids.length) baseFilters.puuid__in = puuids.join(",");
                else baseFilters.competitive_player__in = playerIds.join(",");
                const additional_filters: Record<string, string> = { ...baseFilters };
                if (mapSelected?.id) additional_filters.map = mapSelected.id;

                const recentGamesPromise = Promise.all(
                    roster.map((player) => {
                        const playerPuuids = (accountsByPlayer.get(player.id) || [])
                            .map((account) => account.puuid)
                            .filter(Boolean);
                        if (!playerPuuids.length) return Promise.resolve([] as SoloQGameSummaries[]);
                        const recentFilters: Record<string, string> = {
                            date__lt: until.toISOString(),
                            puuid__in: playerPuuids.join(","),
                        };
                        if (mapSelected?.id) recentFilters.map = mapSelected.id;
                        if (serverSelected) recentFilters.account__server = serverSelected;
                        return ctx.reckonApi.soloqGameSummaries
                            .list({
                                ordering: ["-date"],
                                competitive_player: player.id,
                                additional_filters: recentFilters,
                                only_values: RECENT_GAME_FIELDS as any,
                            })
                            .catch(() =>
                                ctx.reckonApi.soloqGameSummaries.list({
                                    ordering: ["-date"],
                                    competitive_player: player.id,
                                    additional_filters: recentFilters,
                                }),
                            )
                            .then((result) =>
                                asList<SoloQGameSummaries>(result)
                                    .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())
                                    .slice(0, LAST_GAMES),
                            )
                            .catch(() => [] as SoloQGameSummaries[]);
                    }),
                );

                const aggregate = (groupby: string, filters = additional_filters) =>
                    ctx.reckonApi.soloqGameSummaries.aggregate({
                        groupby: groupby as any,
                        ordering: [groupby] as any,
                        aggregates: [STAT_AGGREGATES.join(",")] as any,
                        additional_filters: filters,
                        metrics: [(agentsMetric?.code || "agents")] as any,
                    });

                const [mapResult, statsResult, recentLists] = await Promise.all([
                    aggregate("map", baseFilters).catch(() => []),
                    aggregate("competitive_player").catch(() => []),
                    recentGamesPromise,
                ]);
                if (requestId !== req.current) return;

                const played = [
                    ...new Set(
                        asAggregateRows(mapResult, "map")
                            .map((row) => str(row, "map", "id", "pk"))
                            .filter(Boolean)
                            .map((id) => id.toLowerCase()),
                    ),
                ];
                setMapsWithGames(played);
                if (mapSelected && !played.includes(mapSelected.id.toLowerCase())) {
                    setMapSelected(null);
                    return;
                }

                let statsByPlayer = new Map<string, any>();
                for (const row of asAggregateRows(statsResult, "competitive_player")) {
                    const id = str(row, "competitive_player", "account__player_id", "player_id", "pk", "id");
                    if (id) statsByPlayer.set(id.toLowerCase(), row);
                }

                if (statsByPlayer.size === 0 && puuids.length) {
                    try {
                        const grouped = asAggregateRows(await aggregate("puuid"), "puuid");
                        const merged = new Map<string, any[]>();
                        for (const row of grouped) {
                            const puuid = str(row, "puuid", "pk", "id");
                            const account = [...accountsByPlayer.values()].flat().find((entry) => entry.puuid === puuid);
                            const playerId = account?.player || str(row, "account__player_id", "competitive_player");
                            if (!playerId) continue;
                            const list = merged.get(playerId.toLowerCase()) || [];
                            list.push(row);
                            merged.set(playerId.toLowerCase(), list);
                        }
                        for (const [playerId, list] of merged) {
                            const games = list.reduce((sum, row) => sum + num(row, "games", "pk__cnt", "pk__count"), 0);
                            const weighted = (key: string, fallback: string) => {
                                if (!games) return 0;
                                return (
                                    list.reduce(
                                        (sum, row) =>
                                            sum + num(row, key, fallback) * num(row, "games", "pk__cnt", "pk__count"),
                                        0,
                                    ) / games
                                );
                            };
                            statsByPlayer.set(playerId, {
                                games,
                                win__avg: weighted("win__avg", "win"),
                                acs__avg: weighted("acs__avg", "acs"),
                                adr__avg: weighted("adr__avg", "adr"),
                                rating_vlr__avg: weighted("rating_vlr__avg", "rating_vlr"),
                                kills__avg: weighted("kills__avg", "kills"),
                                deaths__avg: weighted("deaths__avg", "deaths"),
                                agents: list.flatMap((row) => parseAgents(row.agents ?? row.agent, 6)),
                            });
                        }
                    } catch {
                        /* leave empty */
                    }
                }

                if (requestId !== req.current) return;
                const nextRows = roster.map((player, index) => {
                    const accounts = accountsByPlayer.get(player.id) || [];
                    const stats = statsByPlayer.get(player.id.toLowerCase());
                    const kills = num(stats, "kills__avg", "kills");
                    const deaths = num(stats, "deaths__avg", "deaths");
                    return {
                        player,
                        account: pickBestAccount(accounts),
                        accounts: accounts.length,
                        games: num(stats, "games", "pk__cnt", "pk__count"),
                        winRate: hsUnit(num(stats, "win__avg", "win")),
                        acs: num(stats, "acs__avg", "acs"),
                        kd: deaths ? kills / deaths : kills,
                        adr: num(stats, "adr__avg", "adr"),
                        rating: num(stats, "rating_vlr__avg", "rating_vlr", "rating"),
                        agents: parseAgents(stats?.agents ?? stats?.agent),
                        recentGames: recentLists[index] || [],
                        suggestedAccounts: [] as SoloQAccounts[],
                    };
                });
                setRows(nextRows);
                setLoading(false);

                const suggestionLists = await Promise.all(
                    roster.map((player) => {
                        const accounts = accountsByPlayer.get(player.id) || [];
                        if (accounts.length) return Promise.resolve([] as SoloQAccounts[]);
                        return suggestedAccountsForPlayer(
                            (params) => ctx.reckonApi.soloQAccounts.list(params as any),
                            player,
                            linkedByPlayer.get(player.id) || new Set<string>(),
                            { minScore: 50, limit: 3, server: serverSelected },
                        );
                    }),
                );
                if (requestId !== req.current) return;
                if (suggestionLists.some((list) => list.length)) {
                    setRows(
                        nextRows.map((row, index) => ({
                            ...row,
                            suggestedAccounts: suggestionLists[index] || [],
                        })),
                    );
                }
            })
            .catch(() => {
                if (requestId !== req.current) return;
                setRows([]);
                setMapsWithGames([]);
                setLoading(false);
            });

        return () => {
            req.current += 1;
        };
    }, [team?.id, rosterKey, from, until, mapSelected?.id, serverSelected, reloadToken]);

    const onTeamChange = (next: Team | null) => {
        setTeam(next);
        setMapSelected(null);
        if (next) setSearchParams({ team: next.id });
        else setSearchParams({});
    };

    const openPlayer = (player: Player) => {
        ctx.setSelectedPlayer(player);
        navigate(`/soloq-player-profile?player=${encodeURIComponent(player.id)}`);
    };

    const linkAccount = (player: Player, account: SoloQAccounts) => {
        if (!account.puuid) return;
        enqueuePlayerLink({
            playerId: player.id,
            puuid: account.puuid,
            accountName: account.account_name,
            onComplete: (ok) => {
                if (ok && mountedRef.current) setReloadToken((value) => value + 1);
            },
        });
    };

    const teamName = team ? teamLabel(team.name || team.id) : "";

    return (
        <WindowContainer
            spacing={2}
            sx={{
                height: "100%",
                minHeight: 0,
                overflow: "hidden",
                pb: 2,
            }}
        >
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                sx={{ alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", flexShrink: 0 }}
            >
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", minWidth: 0, flex: 1 }}>
                    {team ? <TeamIcon team_name={team.name || team.id} sx={{ width: 44, height: 44, flexShrink: 0 }} /> : null}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                            {teamName || "Team Summary"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {team ? `${team.current_league || "SoloQ"} · roster SoloQ snapshot` : "Select a team"}
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                    <TeamSelect value={team} updateValue={onTeamChange} updateContext />
                    <DatePicker value={from} onChange={(value: Dayjs | null) => value && setFrom(value)} label="From" />
                    <DatePicker value={until} onChange={(value: Dayjs | null) => value && setUntil(value)} label="Until" />
                </Stack>
            </Stack>
            <Stack direction="row" spacing={0.75} sx={{ width: "100%", flexShrink: 0 }}>
                {[null, ...SERVERS].map((value) => {
                    const selected = serverSelected === value;
                    const label = value ?? "All";
                    return (
                        <Box
                            key={label}
                            onClick={() => setServerSelected(value)}
                            sx={{
                                flex: "1 1 0",
                                minWidth: 0,
                                height: 36,
                                borderRadius: "6px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(255,255,255,0.04)",
                                border: selected
                                    ? `1px solid ${neonColor}`
                                    : "1px solid rgba(255,255,255,0.12)",
                                boxShadow: selected ? `0 0 12px ${neonColor}45` : "none",
                                transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                                "&:hover": {
                                    borderColor: `${neonColor}aa`,
                                    boxShadow: `0 0 10px ${neonColor}30`,
                                },
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "0.65rem",
                                    letterSpacing: "0.04em",
                                    textTransform: "uppercase",
                                    color: selected ? neonColor : "rgba(255,255,255,0.75)",
                                }}
                            >
                                {label}
                            </Typography>
                        </Box>
                    );
                })}
            </Stack>
            <MapBannerSelect
                value={mapSelected}
                updateValue={setMapSelected}
                allowClear
                mapsWithGames={mapsWithGames ?? undefined}
            />

            {!team ? (
                <NeonCard>
                    <NeonCardContent>
                        <Typography sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                            Select a team to load its SoloQ summary.
                        </Typography>
                    </NeonCardContent>
                </NeonCard>
            ) : loading && rows.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10, flex: 1 }}>
                    <CircularProgress sx={{ color: neonColor }} />
                </Box>
            ) : roster.length === 0 ? (
                <NeonCard>
                    <NeonCardContent>
                        <NeonEmptyState message="No players found on this team." height="240px" />
                    </NeonCardContent>
                </NeonCard>
            ) : (
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "row",
                        gap: 2,
                        overflowX: "auto",
                        overflowY: "hidden",
                        opacity: loading ? 0.55 : 1,
                        transition: "opacity 0.2s ease",
                        ...scrollable,
                    }}
                >
                    {rows.map((row) => (
                        <PlayerCard
                            key={row.player.id}
                            row={row}
                            onClick={() => openPlayer(row.player)}
                            onLinkAccount={linkAccount}
                        />
                    ))}
                </Box>
            )}
        </WindowContainer>
    );
}
